/**
 * Evaluates one report set: latest metric values → per-gauge state.
 * The live preview renders this directly; the device API compresses it (device-payload.ts).
 * Keeping one function guarantees the preview shows exactly what the device will show.
 */
import type { ClusterModel, Gauge } from './cluster-model.ts';
import type { AssignmentSettings, OrgSettings } from './settings.ts';
import { mapToDial, dialToAngle, round } from './mapping.ts';
import { evaluateStatus, isAlert, zoneColors, type GaugeState } from './rules.ts';
import { isStale, staleAfterMinutes, staleDeadline } from './stale.ts';
import { formatSegment } from './segment.ts';

export interface LatestValue {
  value: number;
  /** epoch ms */
  observedAt: number;
}

export interface AssignmentInput {
  gaugeId: string;
  metricId: string | null;
  settings: AssignmentSettings;
}

export interface ReportSetInput {
  position: number;
  name: string;
  colorMode: 'status' | 'fixed';
  fixedColor: string | null;
  assignments: AssignmentInput[];
}

export interface EvaluateContext {
  model: ClusterModel;
  org: OrgSettings;
  /** Per metric: own stale limit (null = org default). */
  metrics: Record<string, { staleAfterMinutes: number | null }>;
  values: Record<string, LatestValue | undefined>;
  now: number;
}

export interface GaugeView {
  gaugeId: string;
  type: Gauge['type'];
  assigned: boolean;
  value: number | null;
  observedAt: number | null;
  /** epoch ms when the value turns stale (null when there is no value) */
  staleAt: number | null;
  stale: boolean;
  alert: boolean;
  state: GaugeState;
  /** needles */
  dial?: number;
  angle?: number;
  clamped?: 'low' | 'high' | null;
  redlineDial?: number;
  dampingMs?: number;
  /** segment displays */
  text?: string;
  /** warning lights: 0 off, 1 on, 2 blinking */
  light?: 0 | 1 | 2;
}

export interface SetView {
  position: number;
  name: string;
  gauges: Record<string, GaugeView>;
  zones: Record<string, string>;
}

export function evaluateSet(set: ReportSetInput, ctx: EvaluateContext): SetView {
  const { model, org, now } = ctx;
  const byGauge = new Map(set.assignments.map((a) => [a.gaugeId, a]));
  const gauges: Record<string, GaugeView> = {};
  /** Lights switched on by alerts / stale rules of other gauges. */
  const forcedLights = new Map<string, 1 | 2>();
  const force = (id: string, mode: 1 | 2) => forcedLights.set(id, Math.max(forcedLights.get(id) ?? 0, mode) as 1 | 2);

  for (const gauge of model.gauges) {
    const a = byGauge.get(gauge.id);
    const latest = a?.metricId ? ctx.values[a.metricId] : undefined;
    const limit = a?.metricId ? staleAfterMinutes(ctx.metrics[a.metricId]?.staleAfterMinutes, org.stale.after_minutes) : org.stale.after_minutes;
    const assigned = !!a && !!a.metricId;
    const stale = assigned && isStale(latest?.observedAt, now, limit);
    const value = latest && Number.isFinite(latest.value) ? latest.value : null;

    const view: GaugeView = {
      gaugeId: gauge.id,
      type: gauge.type,
      assigned,
      value,
      observedAt: latest?.observedAt ?? null,
      staleAt: latest ? staleDeadline(latest.observedAt, limit) : null,
      stale,
      alert: false,
      state: 'none',
    };

    if (gauge.type === 'needle') {
      const s = a?.settings.kind === 'needle' ? a.settings : undefined;
      view.dampingMs = s?.damping_ms ?? 0;
      if (s?.redline_start !== undefined) view.redlineDial = mapToDial(s.redline_start, s, gauge).dial;
      if (s && value !== null) {
        const pos = mapToDial(value, s, gauge);
        view.dial = pos.dial;
        view.clamped = pos.clamped;
        view.alert = isAlert(value, s.alert);
        view.state = stale ? 'stale' : worstOf(evaluateStatus(value, s.status), view.alert);
        if (view.alert && s.alert?.needle === 'redline') view.dial = view.redlineDial ?? gauge.max;
        if (view.alert && s.alert?.light) force(s.alert.light, s.alert.light_blink ? 2 : 1);
      } else {
        view.dial = gauge.min;
        if (assigned) view.state = 'stale';
      }
      if (stale) {
        if (org.stale.needle === 'min') view.dial = gauge.min;
        if (s?.stale?.light) force(s.stale.light, 1);
      }
      view.angle = round(dialToAngle(view.dial!, gauge), 3);
    } else if (gauge.type === 'segment_display') {
      const s = a?.settings.kind === 'segment' ? a.settings : undefined;
      if (s && value !== null && !stale) {
        view.text = formatSegment(value, gauge, s);
        view.alert = isAlert(value, s.alert);
        view.state = worstOf(evaluateStatus(value, s.status), view.alert);
        if (view.alert && s.alert?.light) force(s.alert.light, s.alert.light_blink ? 2 : 1);
      } else {
        view.text = assigned ? '-'.repeat(gauge.digits) : '';
        if (assigned) view.state = 'stale';
        if (stale && s?.stale?.light) force(s.stale.light, 1);
      }
    } else {
      const s = a?.settings.kind === 'light' ? a.settings : undefined;
      if (s?.mode === 'manual') {
        // Fixed state, no KPI: never stale, never counts as an alert for backlight zones.
        view.assigned = true;
        view.light = ({ off: 0, on: 1, blink: 2 } as const)[s.manual];
      } else {
        const on = s && value !== null && !stale && isAlert(value, { when: s.when, threshold: s.threshold });
        const worse = on && s!.blink_at !== undefined && isAlert(value!, { when: s!.when, threshold: s!.blink_at });
        view.light = on ? (s!.blink || worse ? 2 : 1) : 0;
        view.alert = !!on;
        view.state = stale ? 'stale' : on ? 'alert' : s ? 'ok' : 'none';
      }
    }
    gauges[gauge.id] = view;
  }

  for (const [lightId, mode] of forcedLights) {
    const v = gauges[lightId];
    if (v && v.type === 'warning_light' && (v.light ?? 0) < mode) v.light = mode;
  }

  const states = Object.fromEntries(Object.values(gauges).map((g) => [g.gaugeId, g.state]));
  return {
    position: set.position,
    name: set.name,
    gauges,
    zones: zoneColors(model, states, { colorMode: set.colorMode, fixedColor: set.fixedColor }, org.colors),
  };
}

function worstOf(status: ReturnType<typeof evaluateStatus>, alert: boolean): GaugeState {
  return alert ? 'alert' : status;
}
